import React, { useState, useEffect, useCallback } from "react";
import api from "../../api";

const DIFFICULTY_CONFIG = {
  E: { label: "Easy", bg: "#e6f6ec", fg: "#1f8a4c" },
  M: { label: "Medium", bg: "#fff8ec", fg: "#b07d1a" },
  H: { label: "Hard", bg: "#fef2f2", fg: "#c93c3c" },
};

const modalStyles = {
  overlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0, 0, 0, 0.5)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1300,
  },
  modal: {
    background: "#fff",
    borderRadius: "14px",
    width: "95%",
    maxWidth: "600px",
    height: "auto",
    maxHeight: "85vh",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    boxShadow: "0 8px 32px rgba(0, 0, 0, 0.15)",
  },
  header: {
    padding: "20px 24px",
    borderBottom: "1px solid #e4eaf0",
    display: "flex",
    alignItems: "flex-start",
    gap: "16px",
  },
  headerInfo: {
    flex: 1,
  },
  title: {
    fontSize: "18px",
    fontWeight: 700,
    color: "#16232e",
    margin: 0,
  },
  subtitle: {
    fontSize: "13px",
    color: "#66808f",
    marginTop: "4px",
  },
  closeBtn: {
    background: "none",
    border: "none",
    fontSize: "20px",
    cursor: "pointer",
    color: "#66808f",
    padding: "4px 8px",
  },
  searchBox: {
    padding: "12px 20px",
    borderBottom: "1px solid #e4eaf0",
    background: "#fbfdff",
  },
  searchInput: {
    width: "100%",
    padding: "10px 14px",
    fontSize: "14px",
    border: "1px solid #d5e0e9",
    borderRadius: "8px",
    outline: "none",
  },
  list: {
    minHeight: "300px",
    maxHeight: "400px",
    overflowY: "auto",
  },
  row: {
    padding: "14px 20px",
    borderBottom: "1px solid #f3f6f9",
    display: "flex",
    alignItems: "center",
    gap: "12px",
  },
  number: {
    width: "24px",
    height: "24px",
    borderRadius: "6px",
    background: "#f0f4f7",
    color: "#66808f",
    fontSize: "12px",
    fontWeight: 600,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  rowInfo: {
    flex: 1,
    minWidth: 0,
  },
  rowTitle: {
    fontSize: "14px",
    fontWeight: 600,
    color: "#16232e",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  rowMeta: {
    fontSize: "12px",
    color: "#9db1bf",
    marginTop: "2px",
  },
  diffBadge: {
    fontSize: "11px",
    fontWeight: 700,
    padding: "4px 10px",
    borderRadius: "6px",
  },
  downloadBtn: {
    background: "none",
    border: "1px solid #d5e0e9",
    borderRadius: "6px",
    padding: "6px 12px",
    fontSize: "12px",
    fontWeight: 600,
    color: "#0f6ca8",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: "4px",
  },
  footer: {
    padding: "12px 20px",
    borderTop: "1px solid #eef2f6",
    display: "flex",
    justifyContent: "space-between",
    fontSize: "13px",
    color: "#66808f",
  },
  loading: {
    padding: "40px",
    textAlign: "center",
    color: "#66808f",
  },
  empty: {
    padding: "40px",
    textAlign: "center",
    color: "#66808f",
  },
};

const ViewAssignmentsModal = ({ isOpen, onClose, topicId, topicName, isCourseTemplate = false }) => {
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");

  const fetchAssignments = useCallback(async () => {
    if (!topicId) return;
    setLoading(true);
    try {
      let items = [];

      if (isCourseTemplate) {
        // Cohort mode - use course-template content API
        const response = await api.get(`api/course-template/${topicId}/content/`);
        items = response.data.assignments || [];
      } else {
        // 1:1 mode - use library-assignments API
        const response = await api.get(`api/course/${topicId}/library-assignments/`);
        items = response.data.assignments || [];
      }

      setAssignments(items);
    } catch (err) {
      console.error("Failed to fetch assignments:", err);
    } finally {
      setLoading(false);
    }
  }, [topicId, isCourseTemplate]);

  useEffect(() => {
    if (isOpen) {
      fetchAssignments();
      setSearchQuery("");
    }
  }, [isOpen, fetchAssignments]);

  const handleDownload = async (assignmentId, fileName) => {
    try {
      const response = await api.get(`api/assignment-library/${assignmentId}/download/`);
      const { download_url } = response.data;
      if (download_url) {
        const link = document.createElement("a");
        link.href = download_url;
        link.download = fileName || "assignment.zip";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (err) {
      console.error("Failed to download:", err);
    }
  };

  const filteredAssignments = React.useMemo(() => {
    if (!searchQuery.trim()) return assignments;

    const query = searchQuery.toLowerCase();
    return assignments.filter(
      (a) =>
        a.title?.toLowerCase().includes(query) ||
        a.description?.toLowerCase().includes(query) ||
        a.file_name?.toLowerCase().includes(query)
    );
  }, [assignments, searchQuery]);

  if (!isOpen) return null;

  return (
    <div style={modalStyles.overlay} onClick={() => onClose()}>
      <div style={modalStyles.modal} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div style={modalStyles.header}>
          <div style={modalStyles.headerInfo}>
            <h2 style={modalStyles.title}>
              Assignments — {topicName || "Module"}
            </h2>
            <div style={modalStyles.subtitle}>
              {assignments.length} assignments
            </div>
          </div>
          <button style={modalStyles.closeBtn} onClick={() => onClose()}>
            ✕
          </button>
        </div>

        {/* Search */}
        <div style={modalStyles.searchBox}>
          <input
            type="text"
            style={modalStyles.searchInput}
            placeholder="Search assignments..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        {/* List */}
        <div style={modalStyles.list}>
          {loading ? (
            <div style={modalStyles.loading}>Loading...</div>
          ) : filteredAssignments.length === 0 ? (
            <div style={modalStyles.empty}>
              {assignments.length === 0
                ? "No assignments assigned to this module yet."
                : "No assignments match your search."}
            </div>
          ) : (
            filteredAssignments.map((a, idx) => {
              const diffConfig = DIFFICULTY_CONFIG[a.difficulty] || DIFFICULTY_CONFIG.M;

              return (
                <div key={a.id} style={modalStyles.row}>
                  <span style={modalStyles.number}>{idx + 1}</span>
                  <div style={modalStyles.rowInfo}>
                    <div style={modalStyles.rowTitle}>{a.title}</div>
                    <div style={modalStyles.rowMeta}>
                      {a.file_name || ""} {a.current_version ? `· v${a.current_version}` : ""}
                    </div>
                  </div>
                  <span
                    style={{
                      ...modalStyles.diffBadge,
                      background: diffConfig.bg,
                      color: diffConfig.fg,
                    }}
                  >
                    {diffConfig.label}
                  </span>
                  <button
                    style={modalStyles.downloadBtn}
                    onClick={() => handleDownload(a.id, a.file_name)}
                  >
                    ↓ Download
                  </button>
                </div>
              );
            })
          )}
        </div>

        {/* Footer */}
        <div style={modalStyles.footer}>
          <span>Total assignments</span>
          <span style={{ fontWeight: 600, color: "#16232e" }}>
            {assignments.length}
          </span>
        </div>
      </div>
    </div>
  );
};

export default ViewAssignmentsModal;
